import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../auth/AuthContext';
import api from '../../services/api';

const readAuthToken = () => {
  if (typeof window === 'undefined') return null;
  return window.sessionStorage.getItem('token') || window.localStorage.getItem('token');
};

const Wrap = styled.div`
  position: relative;
`;

const BellButton = styled.button`
  position: relative;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  font-size: 1.25rem;
  padding: 4px 8px;
  line-height: 1;
`;

const Badge = styled.span`
  position: absolute;
  top: 0;
  right: 2px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Panel = styled.div`
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  width: 320px;
  max-height: 400px;
  overflow: auto;
  background: ${({ theme }) => theme.colors.bgElevated};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.lg};
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
  z-index: 1000;
`;

const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 14px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  font-weight: 600;
  font-size: 14px;
`;

const Item = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  padding: 10px 14px;
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ $unread, theme }) => ($unread ? theme.colors.bg : 'transparent')};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  &:hover {
    background: ${({ theme }) => theme.colors.bg};
  }
`;

const ItemTitle = styled.div`
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 2px;
`;

const ItemMsg = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textDim};
`;

const Empty = styled.div`
  padding: 24px;
  text-align: center;
  color: ${({ theme }) => theme.colors.textDim};
  font-size: 13px;
`;

function NotificationBell() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const wrapRef = useRef(null);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.get('/notifications', { params: { limit: 15 } });
      setItems(res.data.notifications || []);
      setUnread(res.data.unreadCount || 0);
    } catch {
      setItems([]);
      setUnread(0);
    }
  }, [user]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, [load]);

  useEffect(() => {
    if (!user) return undefined;
    const token = readAuthToken();
    if (!token) return undefined;

    const base = api.defaults.baseURL || '/api';
    const streamUrl = `${base}/notifications/stream?token=${encodeURIComponent(token)}`;
    const es = new EventSource(streamUrl);

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'notification' && data.notification) {
          setItems((prev) => [data.notification, ...prev].slice(0, 15));
          setUnread((c) => c + 1);
        }
      } catch {
        /* ignore parse errors */
      }
    };

    return () => es.close();
  }, [user]);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const markRead = async (id) => {
    try {
      await api.patch(`/notifications/${id}/read`);
    } catch {
      /* non-blocking */
    }
  };

  const handleItem = async (n) => {
    await markRead(n.id);
    setUnread((c) => Math.max(0, c - (n.read ? 0 : 1)));
    setOpen(false);

    const data = n.data || {};

    if (n.type === 'render_complete') navigate('/render-lab?tab=jobs');
    else if (n.type === 'sale') navigate('/dashboard?tab=products&view=sales');
    else if (n.type === 'order_completed') navigate('/dashboard?tab=purchases');
    else if (n.type === 'support_message') navigate('/dashboard?type=admin&tab=messaging&subTab=userMessages');
    else if (n.type === 'support_reply') navigate('/dashboard?tab=messages&view=support');
    else if (n.type === 'message') navigate('/dashboard?tab=messages');
    else if (n.type === 'product_like' || n.type === 'product_review' || n.type === 'product_comment') {
      navigate(data.productId ? `/product/${data.productId}` : '/dashboard');
    } else if (n.type === 'profile_like' || n.type === 'profile_comment' || n.type === 'new_follower') {
      navigate(data.profileUserId ? `/user/${data.profileUserId}` : '/dashboard');
    } else if (n.type === 'refund_update' || n.type === 'dispute_update') navigate('/dashboard?tab=refunds');
    else navigate('/dashboard');
  };

  if (!user) return null;

  return (
    <Wrap ref={wrapRef}>
      <BellButton type="button" onClick={() => setOpen((o) => !o)} aria-label="Notifications">
        🔔
        {unread > 0 && <Badge>{unread > 9 ? '9+' : unread}</Badge>}
      </BellButton>
      {open && (
        <Panel>
          <PanelHeader>
            <span>Notifications</span>
            {unread > 0 && (
              <button
                type="button"
                style={{ fontSize: 12, background: 'none', border: 'none', color: '#7c5cff', cursor: 'pointer' }}
                onClick={async () => {
                  await api.patch('/notifications/read-all');
                  setUnread(0);
                  setItems((prev) => prev.map((n) => ({ ...n, read: true })));
                }}
              >
                Mark all read
              </button>
            )}
          </PanelHeader>
          {items.length === 0 ? (
            <Empty>No notifications yet</Empty>
          ) : (
            items.map((n) => (
              <Item key={n.id} type="button" $unread={!n.read} onClick={() => handleItem(n)}>
                <ItemTitle>{n.title}</ItemTitle>
                <ItemMsg>{n.message}</ItemMsg>
              </Item>
            ))
          )}
        </Panel>
      )}
    </Wrap>
  );
}

export default NotificationBell;
